import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Sparkles, FileSpreadsheet, Loader2, ArrowRight, CheckCircle2, Clock, FolderOpen,
  Users, ScrollText, PieChart, RefreshCcw, Landmark, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/* MCAAutofillTab — dual-mode:
 *   • New client (no clientId) → drop XLSX dropzone (creates the client).
 *   • Existing client (clientId) → Ingestion Journey timeline: MCA data,
 *     folder, filing index, director-wise Excel uploads, DIN status,
 *     cap table snapshot.
 */
export default function MCAAutofillTab({ clientId }) {
  if (clientId) return <IngestionJourney clientId={clientId} />;
  return <NewClientDropzone />;
}

// ─────────────────────────── NEW CLIENT DROPZONE ───────────────────────────
function NewClientDropzone() {
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/clients/upload-mca-and-create', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(data.message);
      navigate(`/admin/clients/${data.id}/workspace`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'MCA upload failed');
      setUploading(false);
    }
  };

  return (
    <Card className="border-[#0f766e]/30">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="text-[#0f766e]" size={20} />
          Start with MCA Master Data
        </CardTitle>
        <CardDescription>
          Drop the MCA Master XLSX — we&apos;ll create the client, mark it as a Micro CFO retainer, and open the full workspace
          (Filing Index, Directors, Section 167(1) impact, Documents folder).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          onClick={() => !uploading && ref.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            if (!uploading && e.dataTransfer.files[0]) handleUpload(e.dataTransfer.files[0]);
          }}
          data-testid="autofill-drop-mca"
          className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${
            uploading ? 'border-slate-300 bg-slate-50' : 'border-[#0f766e]/40 bg-teal-50 hover:bg-teal-100'
          }`}
        >
          <input
            ref={ref}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => e.target.files[0] && handleUpload(e.target.files[0])}
            data-testid="autofill-input-mca"
          />
          {uploading ? (
            <>
              <Loader2 className="w-8 h-8 mx-auto mb-3 text-[#0f766e] animate-spin" />
              <p className="text-sm font-medium text-slate-700">Creating client &amp; opening workspace…</p>
            </>
          ) : (
            <>
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 text-[#0f766e]" />
              <p className="text-base font-semibold text-slate-800 mb-1">Drop MCA Master Data XLSX here</p>
              <p className="text-sm text-slate-500">or click to browse (.xlsx, max 5 MB)</p>
            </>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1">
          <ArrowRight size={12} />
          Already created a client manually? Use the&nbsp;<strong>Legal Entity</strong>&nbsp;tab on the right to continue without auto-fill.
        </p>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── INGESTION JOURNEY ───────────────────────────
function IngestionJourney({ clientId }) {
  const [client, setClient] = useState(null);
  const [capSummary, setCapSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: c }, sh] = await Promise.all([
        api.get(`/clients/${clientId}`),
        api.get(`/clients/${clientId}/shareholding`).catch(() => ({ data: null })),
      ]);
      setClient(c);
      setCapSummary(sh?.data?.summary || null);
    } catch (e) {
      toast.error('Failed to load ingestion journey');
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [clientId]);

  if (loading && !client) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400 text-sm gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading ingestion journey…
      </div>
    );
  }
  if (!client) {
    return <p className="text-sm text-slate-500 p-4">Client not found.</p>;
  }

  const mca = client.mca_company_info || {};
  const hasMca = !!client.mca_company_info;
  const fi = client.filing_index || {};
  const hasFilingIndex = (fi.total_count || (fi.filings || []).length) > 0;
  const directors = client.mca_directors || [];
  const doc = client.director_other_companies || [];
  const dirUploadedDINs = new Set(doc.map((d) => d.din || d.din_pan).filter(Boolean));

  const formCounts = fi.form_counts || {};
  const topForms = Object.entries(formCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  const activeDins = directors.filter((d) => (d.din_status || '').toLowerCase() === 'active').length;
  const inactiveDins = directors.filter((d) => (d.din_status || '').toLowerCase() && d.din_status !== 'active').length;

  return (
    <Card className="border-[#0f766e]/30" data-testid="ingestion-journey">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 justify-between">
          <span className="flex items-center gap-2">
            <Sparkles className="text-[#0f766e]" size={20} /> MCA Ingestion Journey
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate(`/admin/clients/${clientId}/workspace`)}
              data-testid="open-workspace-btn">
              Open Workspace <ExternalLink size={12} className="ml-1" />
            </Button>
            <Button size="sm" variant="ghost" onClick={refresh} disabled={loading} data-testid="journey-refresh-btn">
              <RefreshCcw size={12} className={loading ? 'animate-spin' : ''} />
            </Button>
          </div>
        </CardTitle>
        <CardDescription>
          Timeline of every data-ingestion artefact for this client. Green = done, amber = pending.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* 1. MCA Master Data */}
        <JourneyRow
          icon={Landmark}
          title="MCA Master Data"
          status={hasMca ? 'done' : 'pending'}
          testId="journey-mca"
          right={hasMca && (
            <Chip color="green">Locked</Chip>
          )}
        >
          {hasMca ? (
            <div className="text-xs text-slate-600 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Entity" value={mca.company_name || '—'} />
              <Stat label="CIN" value={mca.cin || '—'} mono />
              <Stat label="DOI" value={mca.date_of_incorporation || '—'} />
              <Stat label="Status" value={mca.company_status || '—'} />
              <Stat label="Uploaded"
                value={client.mca_uploaded_at
                  ? new Date(client.mca_uploaded_at).toLocaleString('en-IN',
                      { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' })
                  : '—'} />
              <Stat label="File" value={client.mca_file_name || '—'} />
              <Stat label="Small Co" value={mca.small_company ? 'Yes' : '—'} />
              <Stat label="Last BS" value={mca.date_of_balance_sheet || '—'} />
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">MCA XLSX not uploaded yet.</p>
          )}
        </JourneyRow>

        {/* 2. Documents Folder */}
        <JourneyRow
          icon={FolderOpen}
          title="Documents Folder"
          status={client.documents_folder_url ? 'done' : 'pending'}
          testId="journey-folder"
          right={client.documents_folder_url && (
            <a href={client.documents_folder_url} target="_blank" rel="noopener noreferrer"
              className="text-[11px] font-semibold text-amber-600 hover:text-amber-700 flex items-center gap-1"
              data-testid="folder-link">
              Open Folder <ExternalLink size={11} />
            </a>
          )}
        >
          {client.documents_folder_url ? (
            <p className="text-xs text-slate-500 break-all">{client.documents_folder_url}</p>
          ) : (
            <p className="text-xs text-slate-500 italic">
              No Drive folder linked. Add one on the workspace to keep source PDFs alongside parsed data.
            </p>
          )}
        </JourneyRow>

        {/* 3. Filing Index */}
        <JourneyRow
          icon={ScrollText}
          title="Filing Index"
          status={hasFilingIndex ? 'done' : 'pending'}
          testId="journey-filing-index"
          right={hasFilingIndex && (
            <Chip color="indigo">{fi.total_count || (fi.filings || []).length} filings</Chip>
          )}
        >
          {hasFilingIndex ? (
            <div className="text-xs text-slate-600 space-y-1">
              <div className="grid grid-cols-3 gap-2">
                <Stat label="AOC-4 last" value={fi.aoc4_last || '—'} />
                <Stat label="MGT-7 last" value={fi.mgt7_last || '—'} />
                <Stat label="ADT-1 last" value={fi.adt1_last || '—'} />
              </div>
              {topForms.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-1">
                  {topForms.map(([name, n]) => (
                    <span key={name} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                      {name} × {n}
                    </span>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1">
                Uploaded {fi.uploaded_at ? new Date(fi.uploaded_at).toLocaleDateString('en-IN') : '—'}
                {fi.uploaded_by ? ` · by ${fi.uploaded_by}` : ''}
              </p>
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">Filing Index XLSX not uploaded yet.</p>
          )}
        </JourneyRow>

        {/* 4. Director-wise Excel */}
        <JourneyRow
          icon={Users}
          title="Director-wise Excel"
          status={directors.length === 0
            ? 'pending'
            : dirUploadedDINs.size === directors.length ? 'done' : 'partial'}
          testId="journey-directors"
          right={directors.length > 0 && (
            <Chip color={dirUploadedDINs.size === directors.length ? 'green' : 'amber'}>
              {dirUploadedDINs.size} / {directors.length} uploaded
            </Chip>
          )}
        >
          {directors.length === 0 ? (
            <p className="text-xs text-slate-500 italic">No directors parsed from MCA yet.</p>
          ) : (
            <div className="space-y-1">
              {directors.map((d, i) => {
                const din = d.din || d.din_pan;
                const done = dirUploadedDINs.has(din);
                return (
                  <div key={i} className="flex items-center gap-2 text-xs" data-testid={`journey-director-${din}`}>
                    {done
                      ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                      : <Clock className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    <span className="font-medium text-slate-800 min-w-[160px] truncate">{d.name}</span>
                    <span className="text-[10px] font-mono px-1 py-0.5 rounded bg-slate-100 text-slate-500">DIN {din || '—'}</span>
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                      (d.din_status || '').toLowerCase() === 'active'
                        ? 'bg-emerald-50 text-emerald-700'
                        : d.din_status ? 'bg-rose-50 text-rose-700' : 'bg-slate-100 text-slate-400'
                    }`}>{d.din_status || 'unverified'}</span>
                    <span className={`text-[10px] font-semibold ${done ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {done ? 'Director Excel uploaded' : 'Pending Excel upload'}
                    </span>
                  </div>
                );
              })}
              <p className="text-[10px] text-slate-400 mt-1">
                DIN status: {activeDins} active · {inactiveDins} inactive/deactivated
              </p>
            </div>
          )}
        </JourneyRow>

        {/* 5. Cap Table snapshot */}
        <JourneyRow
          icon={PieChart}
          title="Cap Table"
          status={capSummary?.counts?.holdings > 0 ? 'done' : 'pending'}
          testId="journey-cap"
          right={capSummary?.counts?.holdings > 0 && (
            <Chip color="sky">
              {capSummary.counts.share_classes} classes · {capSummary.counts.shareholders} holders
            </Chip>
          )}
        >
          {capSummary?.counts?.holdings > 0 ? (
            <div className="text-xs text-slate-600 grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Stat label="Equity" value={capSummary.total_equity_shares || 0} />
              <Stat label="FDB" value={capSummary.fully_diluted?.fdb_total || 0} />
              <Stat label="Allotments" value={capSummary.counts.allotments || 0} />
              <Stat label="ESOP pool" value={capSummary.esop_pool?.total_pool_size || 0} />
            </div>
          ) : (
            <p className="text-xs text-slate-500 italic">
              No cap-table entries yet. Add classes, shareholders and holdings on the workspace.
            </p>
          )}
        </JourneyRow>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────── PRIMITIVES ───────────────────────────
function JourneyRow({ icon: Icon, title, status, right, children, testId }) {
  const statusMeta = {
    done:    { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', label: 'DONE' },
    partial: { bg: 'bg-amber-50',   border: 'border-amber-200',   dot: 'bg-amber-500',   label: 'PARTIAL' },
    pending: { bg: 'bg-slate-50',   border: 'border-slate-200',   dot: 'bg-slate-300',   label: 'PENDING' },
  }[status] || { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-300', label: 'PENDING' };
  return (
    <div className={`border rounded-lg ${statusMeta.border} ${statusMeta.bg}/50`} data-testid={testId}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-inherit border-opacity-40">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${statusMeta.dot}`} />
          {Icon && <Icon className="w-4 h-4 text-slate-500" />}
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-700">{title}</span>
          <span className="text-[9px] font-bold uppercase text-slate-400">{statusMeta.label}</span>
        </div>
        {right}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}

function Chip({ color, children }) {
  const map = {
    green:  'bg-emerald-100 text-emerald-700 border-emerald-200',
    amber:  'bg-amber-100 text-amber-700 border-amber-200',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    sky:    'bg-sky-100 text-sky-700 border-sky-200',
  };
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full border ${map[color] || map.indigo}`}>
      {children}
    </span>
  );
}

function Stat({ label, value, mono }) {
  return (
    <div>
      <div className="text-[9px] uppercase text-slate-400 tracking-wide">{label}</div>
      <div className={`text-xs font-semibold text-slate-800 truncate ${mono ? 'font-mono' : ''}`} title={String(value)}>
        {value || '—'}
      </div>
    </div>
  );
}
